import type {
  EditGroupDraft,
  EditGroupMember,
  GroupRow
} from '../models/management.model';

export function hasGroupDraftChanges(draft: EditGroupDraft | null, original: EditGroupDraft | null): boolean {
  if (!draft || !original) {
    return false;
  }

  return (
    draft.name !== original.name ||
    draft.description !== original.description ||
    draft.websiteUrl !== original.websiteUrl ||
    draft.sport !== original.sport ||
    draft.coverPhotoUrl !== original.coverPhotoUrl ||
    !!draft.coverPhotoFile
  );
}

export function canSaveGroupDraftChanges(
  draft: EditGroupDraft | null,
  original: EditGroupDraft | null,
  options: { requireDescription?: boolean } = {}
): boolean {
  const requireDescription = options.requireDescription ?? true;
  return (
    hasGroupDraftChanges(draft, original) &&
    !!draft?.name.trim() &&
    (!requireDescription || !!draft?.description.trim()) &&
    isGroupDraftWebsiteUrlValid(draft.websiteUrl)
  );
}

export function isGroupDraftWebsiteUrlValid(value: string | null | undefined): boolean {
  const trimmed = value?.trim() ?? '';
  if (!trimmed) {
    return true;
  }

  if (!/^https?:\/\//i.test(trimmed) || /\s/.test(trimmed)) {
    return false;
  }

  try {
    const url = new URL(trimmed);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
}

export function updateDraftCoverPhoto(
  draft: EditGroupDraft | null,
  coverPhotoUrl: string | null,
  coverPhotoFile?: File | null
): EditGroupDraft | null {
  return draft ? { ...draft, coverPhotoUrl, coverPhotoFile: coverPhotoFile ?? undefined } : null;
}

export function resetDraftFromOriginal(original: EditGroupDraft | null): EditGroupDraft | null {
  return original ? { ...original } : null;
}

export function updateDraftField<K extends keyof EditGroupDraft>(
  draft: EditGroupDraft | null,
  field: K,
  value: EditGroupDraft[K]
): EditGroupDraft | null {
  return draft ? { ...draft, [field]: value } : null;
}

export function restoreDraftField<K extends keyof EditGroupDraft>(
  draft: EditGroupDraft | null,
  original: EditGroupDraft | null,
  field: K
): EditGroupDraft | null {
  return draft && original ? updateDraftField(draft, field, original[field]) : draft;
}

export function getPreviewItems<T>(items: T[], expanded: boolean, previewCount: number): T[] {
  return expanded ? items : items.slice(0, previewCount);
}

export function getPreviewMoreCount(total: number, expanded: boolean, previewCount: number): number {
  return expanded ? 0 : Math.max(0, total - previewCount);
}

export function canTogglePreviewList(total: number, expanded: boolean, previewCount: number): boolean {
  return expanded || getPreviewMoreCount(total, expanded, previewCount) > 0;
}

export function getMainAdminMember(
  draft: EditGroupDraft | null,
  selectedGroup: GroupRow | null,
  groups: GroupRow[]
): EditGroupMember | null {
  if (!draft) {
    return selectedGroup
      ? {
        name: selectedGroup.owner?.displayName ?? selectedGroup.admin.name,
        email: selectedGroup.owner?.email ?? selectedGroup.admin.email,
        role: 'Main admin'
      }
      : null;
  }

  const group = groups.find(item => item.id === draft.id) ?? selectedGroup;
  return group
    ? {
      name: group.owner?.displayName ?? group.admin.name,
      email: group.owner?.email ?? group.admin.email,
      role: 'Main admin'
    }
    : null;
}

export function orderMembersWithMainAdmin(
  members: EditGroupMember[],
  mainAdmin: EditGroupMember | null
): EditGroupMember[] {
  if (!mainAdmin) {
    return members;
  }

  const isSameMember = (member: EditGroupMember) =>
    member.email.toLowerCase() === mainAdmin.email.toLowerCase() ||
    member.name.trim().toLowerCase() === mainAdmin.name.trim().toLowerCase();
  const remainingMembers = members
    .filter(member => !isSameMember(member))
    .map(member => ({ ...member, role: member.role === 'Main admin' ? undefined : member.role }));

  return [{ ...mainAdmin, role: 'Main admin' }, ...remainingMembers];
}
